import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/util/cn';
import { RegularAd } from '@/tracking/ads/components/RegularAd';
import { IResortReviews, ReviewsOrderBy } from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Diagnostics } from '@/util/diagnostics';
import { fetchResortReviewsPages } from '@/api/resort/static';
import { Review } from '@/components/resort/Review';
import { CtaButton } from '@/components/common/CtaButton';
import { isValidResponse } from '@/util/isValidResponse';
import { I18nKey } from '@/i18n/types';

// List of individual reviews on the Resort Reviews page. Owns:
//   • the "All reviews" header + sort-by dropdown (was a separate
//     SortReviewsRating component built on react-select; now a
//     native <select>)
//   • the paginated review feed with a RegularAd every 4 reviews
//   • the Load More button + spinning loader state

const AD_EVERY_REVIEWS = 8;

const SORT_OPTIONS: { value: ReviewsOrderBy; labelKey: I18nKey['common'] }[] = [
  { value: ReviewsOrderBy.date, labelKey: 'resort.reviews.mostRecent' },
  { value: ReviewsOrderBy.highestRating, labelKey: 'resort.reviews.highestRating' },
  { value: ReviewsOrderBy.lowestRating, labelKey: 'resort.reviews.lowestRating' },
];

interface IProps {
  reviewsList: IResortReviews;
  uuid: string;
  className?: string;
}

export const ReviewsList = ({ reviewsList, uuid, className }: IProps) => {
  const { t } = useOTSTranslation('common');
  const [sortBy, setSortBy] = useState<ReviewsOrderBy>(ReviewsOrderBy.date);
  const [isLoading, setLoading] = useState(false);
  const [pages, setPages] = useState<Array<IResortReviews>>([reviewsList]);
  const lastPage = Math.ceil(pages[0].pagination.count / pages[0].pagination.limit);

  const loadNextPage = async () => {
    setLoading(true);
    const next: IResortReviews = await fetchResortReviewsPages(uuid, sortBy, pages.length);
    if (!isValidResponse(next, ['pagination', 'reviewsList'])) {
      Diagnostics.error({ message: 'Server returned unknown report page type', tag: 'resort-reports' });
      setLoading(false);
      return;
    }
    setPages((prev) => [...prev, next]);
    setLoading(false);
  };

  useEffect(() => {
    const loadSorted = async () => {
      const sorted = await fetchResortReviewsPages(uuid, sortBy, 0);
      if (!isValidResponse(sorted, ['pagination', 'reviewsList'])) {
        Diagnostics.error({ message: 'Server returned unknown report page type', tag: 'resort-reports' });
        return;
      }
      setPages([sorted]);
    };
    loadSorted().catch(() => {});
  }, [sortBy]);

  return (
    <section className={cn(className)}>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-heading text-xl text-secondary md:text-2xl">
          {t('resort.reviews.allReviews')}
        </h2>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <label htmlFor="reviews-sort-by">{t('resort.reviews.orderBy')}</label>
          <select
            id="reviews-sort-by"
            className={cn(
              'rounded-md border border-border bg-background px-3 py-1.5',
              'text-sm font-medium text-foreground',
              'hover:border-primary/40 focus:border-primary',
              'focus:outline-none focus:ring-1 focus:ring-primary',
            )}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as ReviewsOrderBy)}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        {pages.map((page, pageIndex) => page.reviewsList.map((review, index) => {
          const shouldInsertAd = ((pageIndex * 10) + index + 1) % AD_EVERY_REVIEWS === 0;
          return (
            <React.Fragment key={`${review.uuid}-${review.date}-${review.review}`}>
              <Review review={review} />
              {shouldInsertAd && <RegularAd />}
            </React.Fragment>
          );
        }))}
      </div>

      {pages.length !== lastPage && (
        <div className="mt-6">
          <CtaButton
            onClick={loadNextPage}
            disabled={isLoading}
            className="w-full font-bold"
            iconLeft={isLoading ? <Loader2 className="animate-spin" aria-hidden /> : undefined}
            iconRight={null}
          >
            {t('common.loadMore')}
          </CtaButton>
        </div>
      )}
    </section>
  );
};
